import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ChatCircle, MagnifyingGlass, Spinner, ArrowClockwise, Phone,
  Trash, Archive, Moon, DotsThreeVertical, Check, Checks,
  Warning, Prohibit,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import {
  fetchAllConversations, markConversationSeen, deleteConversation,
  archiveConversation, unarchiveConversation, autoArchiveStaleThreads,
  snoozeConversation, markConversationUnread, blockNumber, unblockNumber,
  fetchBlockedNumbers,
} from "@/lib/api";
import SmsChatView from "@/components/SmsChatView";
import AccountMenu from "@/components/AccountMenu";
import { HelpPill } from "@/components/HeaderBrand";
import { useT } from "@/lib/i18n";
import { extractErr } from "@/lib/errors";
import { toast } from "sonner";

/**
 * ConversationsPage — global SMS inbox in a WhatsApp-Web layout.
 * --------------------------------------
 *  • Left rail (~360px): searchable conversation list.
 *  • Right pane: the active conversation rendered inline as a full chat view.
 *  • On mobile (<md), the two panes swap: tapping a row replaces the list
 *    with the chat; an in-header back button returns to the list.
 *
 * The legacy modal SmsChatPanel is no longer used here. CustomerPage still
 * uses it as a side drawer when texting a contact directly from the customer
 * detail view.
 */
export default function ConversationsPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("active"); // "active" | "archived"
  const [active, setActive] = useState(null); // selected thread
  // In-app confirm-delete dialog state. Replaces window.confirm() because
  // the preview environment hosts the app inside an iframe where browsers
  // can silently block native dialogs — the user-visible symptom is "click
  // delete, nothing happens".
  const [confirmDelete, setConfirmDelete] = useState(null); // {row} | null
  const [deleting, setDeleting] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(null); // {row} | null
  const [blocking, setBlocking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { items: rows } = await fetchAllConversations(200, view === "archived");
      setItems(rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load conversations");
    } finally {
      setLoading(false);
    }
  }, [view]);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh the list every 15s so new inbound replies surface without a
  // manual click. Only refreshes the LIST — the chat pane has its own polling.
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) =>
      [r.company_name, r.person_name, r.phone_number, r.last_body]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, search]);

  const openConversation = (r) => {
    setActive({
      key: keyForRow(r),
      customerId: r.customer_id,
      customerName: r.company_name || r.customer_id || (r.customer_id ? "Unknown" : ""),
      personId: r.person_id,
      personName: r.person_name || "",
      personPhone: r.phone_number || "",
      archived: !!r.archived,
      archivedReason: r.archived_reason || null,
    });
    if (r.unread_count > 0 && r.customer_id) {
      markConversationSeen(r.customer_id, r.person_id).catch(() => {});
      setItems((prev) => prev.map((p) =>
        (p.customer_id === r.customer_id && p.person_id === r.person_id && p.phone_number === r.phone_number)
          ? { ...p, unread_count: 0 } : p
      ));
    }
  };

  // ─── row actions ────────────────────────────────────────────────
  const dropRow = (r) => setItems((prev) =>
    prev.filter((p) => !(p.customer_id === r.customer_id && p.person_id === r.person_id && p.phone_number === r.phone_number))
  );

  const handleArchive = async (r) => {
    try {
      await archiveConversation(r.customer_id, r.person_id, r.phone_number);
      dropRow(r);
      if (active?.customerId === r.customer_id
          && active?.personId === r.person_id
          && active?.personPhone === r.phone_number) {
        setActive(null);
      }
    } catch (err) {
      toast.error(`Archive failed: ${extractErr(err, "unknown error").message}`);
    }
  };
  const handleSnooze = async (r) => {
    try {
      await snoozeConversation(r.customer_id, r.person_id, r.phone_number);
      dropRow(r);
      if (active?.customerId === r.customer_id
          && active?.personId === r.person_id
          && active?.personPhone === r.phone_number) {
        setActive(null);
      }
    } catch (err) {
      toast.error(`Snooze failed: ${extractErr(err, "unknown error").message}`);
    }
  };
  const handleUnarchive = async (r) => {
    try {
      await unarchiveConversation(r.customer_id, r.person_id, r.phone_number);
      dropRow(r);
      if (active?.customerId === r.customer_id
          && active?.personId === r.person_id
          && active?.personPhone === r.phone_number) {
        setActive(null);
      }
    } catch (err) {
      toast.error(`Unarchive failed: ${extractErr(err, "unknown error").message}`);
    }
  };
  const handleDelete = (r) => {
    // Open the in-app confirm dialog instead of window.confirm() — the
    // native dialog can be silently blocked when the app is loaded inside
    // an iframe (which is exactly what the Emergent preview UI does), so
    // users would click delete and see nothing happen.
    setConfirmDelete(r);
  };
  const handleMarkUnread = async (r) => {
    try {
      await markConversationUnread(r.customer_id, r.person_id, r.phone_number);
      // Refresh the list so the green dot reappears + flip the active
      // chat away (so we don't immediately re-mark it as seen via openConversation).
      if (active?.customerId === r.customer_id
          && active?.personId === r.person_id
          && active?.personPhone === r.phone_number) {
        setActive(null);
      }
      load();
    } catch (err) {
      toast.error(`Mark unread failed: ${extractErr(err, "unknown error").message}`);
    }
  };
  const handleBlock = (r) => {
    if (!r.phone_number) {
      toast.error("Cannot block — no phone number on this thread.");
      return;
    }
    setConfirmBlock(r);
  };
  const confirmBlockRow = async () => {
    const r = confirmBlock;
    if (!r) return;
    setBlocking(true);
    try {
      const res = await blockNumber(r.phone_number, null);
      if (active?.personPhone === r.phone_number) setActive(null);
      setConfirmBlock(null);
      load();
      // Friendly inline confirmation via alert (one-off, fast).
      // The thread silently disappears from active; the alert avoids any
      // "did anything happen?" ambiguity.
      window.setTimeout(() => alert(
        `Blocked ${res.phone}. ${res.threads_archived} thread(s) auto-archived.`
      ), 120);
    } catch (err) {
      toast.error(`Block failed: ${extractErr(err, "unknown error").message}`);
    } finally {
      setBlocking(false);
    }
  };
  const handleUnblock = async (r) => {
    if (!r.phone_number) return;
    try {
      await unblockNumber(r.phone_number);
      toast.success(`Unblocked ${r.phone_number}. Thread restored to active.`);
      load();
    } catch (err) {
      toast.error(`Unblock failed: ${extractErr(err, "unknown error").message}`);
    }
  };

  const confirmDeleteRow = async () => {
    const r = confirmDelete;
    if (!r) return;
    setDeleting(true);
    try {
      await deleteConversation(r.customer_id, r.person_id, r.phone_number);
      dropRow(r);
      if (active?.customerId === r.customer_id
          && active?.personId === r.person_id
          && active?.personPhone === r.phone_number) {
        setActive(null);
      }
      setConfirmDelete(null);
    } catch (err) {
      toast.error(`Delete failed: ${extractErr(err, "unknown error").message}`);
    } finally {
      setDeleting(false);
    }
  };

  const onAutoArchive = async () => {
    const days = window.prompt("Archive every active SMS thread with no activity for at least N days:", "90");
    const n = parseInt(days, 10);
    if (!n || n < 7) return;
    try {
      const res = await autoArchiveStaleThreads(n);
      alert(`Auto-archive complete — ${res.archived} thread(s) archived, ${res.checked} checked.`);
      load();
    } catch (e) {
      toast.error(`Auto-archive failed: ${extractErr(e, "unknown error").message}`);
    }
  };

  // ─── render ─────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col" style={{ background: "#f0f2f5" }}>
      {/* Top bar (slim — primary nav stays the same as the rest of the app) */}
      <div className="h-12 px-3 md:px-6 flex items-center gap-3 shrink-0 bg-white border-b border-slate-200">
        <button
          onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title={t("action.back", "Back")}
        >
          <ArrowLeft size={18} weight="bold" />
        </button>
        <ChatCircle size={18} weight="fill" style={{ color: "#00a884" }} />
        <h1 className="text-sm md:text-base font-black tracking-tight font-['Chivo']">
          Conversations
        </h1>
        <span className="text-xs text-slate-400 font-mono hidden sm:inline">
          {filtered.length}/{items.length}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <HelpPill />
          <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>
      </div>

      {/* WhatsApp-Web 2-pane layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left rail — list */}
        <aside
          className={`flex flex-col bg-white border-r border-slate-200 w-full md:w-[380px] shrink-0
            ${active ? "hidden md:flex" : "flex"}`}
          data-testid="conversations-list-pane"
        >
          {/* List header */}
          <div className="px-3 py-2 flex items-center gap-2 shrink-0" style={{ background: "#f0f2f5" }}>
            <div className="inline-flex items-center rounded-full overflow-hidden text-[11px] font-bold uppercase tracking-wider bg-white border border-slate-200">
              <button
                onClick={() => setView("active")}
                data-testid="conversations-view-active"
                className={`px-3 h-7 transition-colors ${view === "active" ? "text-white" : "text-slate-500 hover:bg-slate-100"}`}
                style={view === "active" ? { background: "#00a884" } : undefined}
              >
                Active
              </button>
              <button
                onClick={() => setView("archived")}
                data-testid="conversations-view-archived"
                className={`px-3 h-7 transition-colors inline-flex items-center gap-1 ${view === "archived" ? "bg-amber-500 text-white" : "text-slate-500 hover:bg-slate-100"}`}
              >
                <Archive size={11} weight="bold" />
                Archived
              </button>
            </div>
            <button
              onClick={load}
              disabled={loading}
              className={`ml-auto w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 ${loading ? "text-emerald-600" : "text-slate-500"}`}
              title={loading ? "Refreshing…" : "Reload"}
              data-testid="conversations-reload-btn"
            >
              <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={onAutoArchive}
              data-testid="conversations-auto-archive-btn"
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500 hover:text-amber-700"
              title={t("conversations.auto_archive_tip", "Auto-archive stale threads")}
            >
              <DotsThreeVertical size={20} weight="bold" />
            </button>
          </div>

          {/* Search */}
          <div className="px-3 py-2 shrink-0 bg-white">
            <div className="relative">
              <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="conversations-search"
                placeholder={t("conversations.search_placeholder", "Search or start new conversation")}
                className="pl-9 h-9 rounded-full border-slate-200 text-sm bg-slate-50"
                style={{ background: "#f0f2f5" }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto" data-testid="conversations-list">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <Spinner size={24} className="animate-spin text-slate-400" />
              </div>
            ) : error ? (
              <div className="m-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-3">{error}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-500 px-6" data-testid="conversations-empty">
                <ChatCircle size={36} weight="duotone" className="text-slate-300 mx-auto mb-3" />
                <div className="text-sm font-medium">{t("conversations.empty_title", "No SMS conversations")}</div>
                <div className="text-xs mt-1">{t("conversations.empty_subtitle", "Send a Text to any contact and the thread will appear here.")}</div>
              </div>
            ) : (
              filtered.map((r) => {
                const orphan = !r.customer_id;
                const key = keyForRow(r);
                const selected = active?.key === key;
                const unread = r.unread_count > 0;
                const failed = ["failed", "undelivered"].includes((r.last_status || "").toLowerCase());
                const dt = r.last_message_at ? new Date(r.last_message_at) : null;
                return (
                  <div
                    key={key}
                    className={`group flex items-stretch border-b border-slate-100 transition-colors cursor-pointer ${
                      selected ? "bg-slate-100" : "hover:bg-slate-50"
                    }`}
                  >
                    <button
                      onClick={() => openConversation(r)}
                      data-testid={r.customer_id
                        ? `conversation-row-${r.customer_id}-${r.person_id || "nopid"}-${r.phone_number || "nophone"}`
                        : `conversation-row-orphan-${r.phone_number || r.person_id || "_"}`}
                      title={orphan ? `Inbound from ${r.phone_number || "unknown"} — not yet linked to a customer.` : undefined}
                      className="flex-1 text-left flex items-start gap-3 px-3 py-3 min-w-0"
                    >
                      <div
                        className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-base"
                        style={{ background: orphan ? "#94a3b8"
                                    : avatarColor(r.company_name || r.customer_id || r.person_name || "") }}
                      >
                        {orphan
                          ? "?"
                          : (r.company_name || r.person_name || "?").trim().charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[14px] truncate flex-1 ${unread ? "font-bold text-slate-900" : "font-medium text-slate-800"}`}>
                            {r.company_name || (orphan ? "Unknown" : (r.customer_id || "Unknown"))}
                          </span>
                          {dt && (
                            <span className={`text-[11px] shrink-0 ${unread ? "font-bold text-emerald-700" : "text-slate-500"}`}>
                              {formatListTime(dt)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {r.last_direction === "outbound" && (
                            <DeliveryTick status={r.last_status} failed={failed} small />
                          )}
                          <span className={`flex-1 text-[13px] truncate ${failed ? "text-rose-600" : unread ? "text-slate-800" : "text-slate-500"}`}>
                            {r.last_direction === "outbound" ? "" : ""}{r.last_body || "(no body)"}
                          </span>
                          {unread && (
                            <span
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white shrink-0"
                              style={{ background: "#00a884" }}
                            >
                              {r.unread_count}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1.5 truncate">
                          {r.person_name && <span className="truncate">{r.person_name}</span>}
                          {r.phone_number && (
                            <span className="inline-flex items-center gap-0.5 font-mono text-[10px]">
                              <Phone size={9} weight="fill" />
                              {r.phone_number}
                            </span>
                          )}
                          {r.archived && r.archived_by === "system" && (
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200 inline-flex items-center gap-1"
                              title={r.archived_reason ? `Auto-archived (${r.archived_reason})` : "Auto-archived"}>
                              <Archive size={8} weight="bold" /> Auto
                            </span>
                          )}
                          {r.archived && r.archived_reason === "snoozed" && (
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-violet-100 text-violet-700 border border-violet-200 inline-flex items-center gap-1"
                              title={t("conversations.snoozed_tip", "Snoozed — will return to Active when this contact replies")}>
                              <Moon size={8} weight="fill" /> Snoozed
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* Right pane — chat */}
        <section
          className={`flex-1 flex flex-col min-w-0 ${active ? "flex" : "hidden md:flex"}`}
          data-testid="conversations-chat-pane"
        >
          <SmsChatView
            key={active?.key || "empty"}
            customerId={active?.customerId}
            customerName={active?.customerName}
            personId={active?.personId}
            personName={active?.personName}
            personPhone={active?.personPhone}
            archived={active?.archived}
            onBack={() => setActive(null)}
            onAfterSend={() => load()}
            onArchive={active ? () => handleArchive({
              customer_id: active.customerId, person_id: active.personId,
              phone_number: active.personPhone,
            }) : undefined}
            onSnooze={active ? () => handleSnooze({
              customer_id: active.customerId, person_id: active.personId,
              phone_number: active.personPhone,
            }) : undefined}
            onUnarchive={active ? () => handleUnarchive({
              customer_id: active.customerId, person_id: active.personId,
              phone_number: active.personPhone,
            }) : undefined}
            onMarkUnread={active ? () => handleMarkUnread({
              customer_id: active.customerId, person_id: active.personId,
              phone_number: active.personPhone,
            }) : undefined}
            onBlock={active?.personPhone && active.archivedReason !== "blocked"
              ? () => handleBlock({
                  customer_id: active.customerId, person_id: active.personId,
                  phone_number: active.personPhone,
                })
              : undefined}
            onUnblock={active?.personPhone && active.archivedReason === "blocked"
              ? () => handleUnblock({phone_number: active.personPhone}).then(() => setActive(null))
              : undefined}
            onDelete={active ? () => handleDelete({
              customer_id: active.customerId, person_id: active.personId,
              phone_number: active.personPhone,
              company_name: active.customerName,
              person_name: active.personName,
              message_count: undefined, // chat doesn't track count; modal copy degrades gracefully
            }) : undefined}
            onLinked={(res) => {
              // Phone just got attached to a person — flip the chat into
              // known mode (so suggestions, status refresh, customer-scoped
              // features all activate) and refresh the list so the orphan
              // row drops out and re-emerges as a normal customer row.
              setActive((prev) => prev ? {
                ...prev,
                key: [res.customer_id, res.pfcid, res.phone].join("__"),
                customerId: res.customer_id,
                customerName: res.company_name || prev.customerName,
                personId: res.pfcid,
                personName: res.name || prev.personName,
              } : prev);
              load();
            }}
            emptyHint="Pick a conversation from the left to read replies and send a new SMS."
          />
        </section>
      </div>

      {/* In-app delete-confirm dialog. We avoid window.confirm() because
          the preview environment runs the app inside an iframe where
          some browsers silently block native dialogs — that was the root
          cause of "click delete, nothing happens". */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4"
          style={{ background: "rgba(15, 23, 42, 0.6)" }}
          onClick={() => !deleting && setConfirmDelete(null)}
          data-testid="conversation-delete-confirm"
        >
          <div
            className="bg-white rounded-md shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                <Warning size={20} weight="fill" className="text-rose-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold tracking-tight font-['Chivo']">
                  {t("conversations.delete_title", "Delete this conversation?")}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {t("conversations.delete_removes", "Permanently removes")}
                  {typeof confirmDelete.message_count === "number" && (
                    <> <span className="font-bold">{confirmDelete.message_count} message{confirmDelete.message_count === 1 ? "" : "s"}</span> from</>
                  )}
                  {" "}{t("conversations.delete_the_thread", "the thread")}{" "}
                  {confirmDelete.company_name || confirmDelete.person_name
                    ? <>with <span className="font-bold">{confirmDelete.company_name || confirmDelete.person_name}</span></>
                    : <>{t("conversations.delete_with_contact", "with this contact")}</>}
                  {confirmDelete.phone_number && <> at <span className="font-mono">{confirmDelete.phone_number}</span></>}.
                </p>
                <p className="text-[11px] text-slate-400 mt-2">
                  {t("conversations.delete_twilio_note", "Twilio's own log is untouched — only this app's copy is removed. This cannot be undone.")}
                </p>
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2 bg-slate-50 rounded-b-md">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                data-testid="conversation-delete-cancel"
                className="h-8 px-4 text-xs font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-200 rounded-sm"
              >{t("action.cancel", "Cancel")}</button>
              <button
                type="button"
                onClick={confirmDeleteRow}
                disabled={deleting}
                data-testid="conversation-delete-confirm-btn"
                className="h-8 px-4 text-xs font-bold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {deleting ? <Spinner size={11} className="animate-spin" /> : <Trash size={11} weight="bold" />}
                {t("action.delete", "Delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* In-app block-number confirm dialog. Same iframe-safety reasoning
          as the delete modal — we avoid window.confirm here too. */}
      {confirmBlock && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4"
          style={{ background: "rgba(15, 23, 42, 0.6)" }}
          onClick={() => !blocking && setConfirmBlock(null)}
          data-testid="conversation-block-confirm"
        >
          <div className="bg-white rounded-md shadow-2xl w-full max-w-md"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                <Warning size={20} weight="fill" className="text-rose-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold tracking-tight font-['Chivo']">
                  Block <span className="font-mono">{confirmBlock.phone_number}</span>?
                </h3>
                <ul className="text-xs text-slate-500 mt-1 list-disc pl-4 space-y-0.5">
                  <li>{t("conversations.block_bullet_inbound", "Future inbound from this number is silently archived (no green dot, no notification).")}</li>
                  <li>Outbound <code className="font-mono">/sms/send</code> to this number returns 403.</li>
                  <li>{t("conversations.block_bullet_history", "Existing thread history stays — you can unblock anytime from the chat menu.")}</li>
                </ul>
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2 bg-slate-50 rounded-b-md">
              <button type="button" onClick={() => setConfirmBlock(null)} disabled={blocking}
                data-testid="conversation-block-cancel"
                className="h-8 px-4 text-xs font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-200 rounded-sm">{t("action.cancel", "Cancel")}</button>
              <button type="button" onClick={confirmBlockRow} disabled={blocking}
                data-testid="conversation-block-confirm-btn"
                className="h-8 px-4 text-xs font-bold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                {blocking ? <Spinner size={11} className="animate-spin" /> : <Prohibit size={11} weight="bold" />}
                {t("conversations.block_number", "Block number")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────

function DeliveryTick({ status, failed, small }) {
  const sz = small ? 12 : 14;
  const s = (status || "").toLowerCase();
  if (failed) return <span className="text-rose-600 text-[11px] font-bold" title={status}>!</span>;
  if (s === "read") return <Checks size={sz} weight="bold" className="text-sky-500" />;
  if (s === "delivered" || s === "received") return <Checks size={sz} weight="bold" className="text-slate-400" />;
  if (s === "sent") return <Check size={sz} weight="bold" className="text-slate-400" />;
  return null;
}

function formatListTime(d) {
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (sameDay(d, yesterday)) return "Yesterday";
  const diff = (today - d) / (1000 * 60 * 60 * 24);
  if (diff < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { day: "2-digit", month: "2-digit", year: "2-digit" });
}

// Stable per-name avatar background — picks from a curated WhatsApp-friendly palette.
const AVATAR_PALETTE = [
  "#00a884", "#1f7aec", "#9c27b0", "#ff9800", "#f44336",
  "#3f51b5", "#009688", "#795548", "#607d8b", "#e91e63",
];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

// Stable thread key: (customer_id, person_id, phone_number). Orphan rows use
// just the phone_number — main vs alt of the same person are kept apart.
function keyForRow(r) {
  return [
    r.customer_id || "_",
    r.person_id || "_",
    r.phone_number || "_",
  ].join("__");
}
